/* eslint-disable jsx-a11y/anchor-is-valid */
import moment from 'moment'
import {FC, useEffect, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {KTCard} from '../../../../../../../../_metronic/helpers'
import {Button, ButtonWrapper} from '../../../../../../components/UI/Button'
import {InputDateWrapper, SelectWrapper} from '../../../../../../components/UI/Form/Input'
import {censusHistorySchema} from '../../../../../../util/validationLimpieza'
import {useCampaments} from '../../../../hooks/central/useCampaments'
import {useClients} from '../../../../hooks/central/useClients'
import {useCompanies} from '../../../../hooks/central/useCompanies'
import {useSites} from '../../../../hooks/central/useSites'
import {useModules} from '../../../../hooks/hoteleria/useModules'
import {CensusHistoryModel, CensusHistoryResponse} from '../../../../model/census.model'
import {CENSUS_SERVICES} from '../../../../services/census.service'
import {CensusHistoryTable} from '../table/census-history'
import { getSnackBarToast } from '../../../../../../util/snackBar'
const CENSUS_MODULO_TODOS_VALUE = '4EsB2NAAsDA='

const getTodayDateInputValue = (): string => {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const CensusHistoryForm: FC = () => {
  const {getCompanies, companies, clearCompanies} = useCompanies()
  const {getClients, clients, clearClients} = useClients()
  const {getSites, sites, clearSites} = useSites()
  const {getCampaments, campaments, clearCampaments} = useCampaments()
  const {getModules, modules, clearModules} = useModules()
  const [dataCensus, setDataCensus] = useState<CensusHistoryResponse[]>([])
  const [selectedCensus, setSelectedCensus] = useState<CensusHistoryResponse | null>(null)
  const [isDownloadXLS, setIsDownloadXLS] = useState<boolean>(false)

  const methods = useForm<any>({
    resolver: yupResolver(censusHistorySchema),
    defaultValues: {
      CompaniaId: '',
      ClienteId: '',
      SiteId: '',
      CampamentoId: '',
      ModuloId: '',
      FechCenso: '',
    },
  })

  const {setValue, handleSubmit, setError, watch, control} = methods
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    if (companies.length === 1) {
      setValue('CompaniaId', companies[0].value)
      setError('CompaniaId', {})
      getClients({isCombo: true, companiaId: companies[0].value}, 1)
    }
  }, [companies])

  useEffect(() => {
    if (clients.length === 1) {
      setValue('ClienteId', clients[0].value)
      setError('ClienteId', {})
      getSites({isCombo: true, clienteId: clients[0].value}, 1)
    }
  }, [clients])

  useEffect(() => {
    if (sites.length === 1) {
      setValue('SiteId', sites[0].value)
      setError('SiteId', {})
      getCampaments({isCombo: true, siteId: sites[0].value}, 1)
    }
  }, [sites])

  useEffect(() => {
    if (campaments.length === 1) {
      setValue('CampamentoId', campaments[0].value)
      setError('CampamentoId', {})
      getModules(
        {CampamentoId: campaments[0].value, pageNumber: 1, size: 100},
        {all: true, value: '4EsB2NAAsDA=', label: 'TODOS'},
      )
    }
  }, [campaments])

  useEffect(() => {
    getCompanies({isCombo: true}, 1)
    return () => {
      clearCompanies()
      clearClients()
      clearSites()
      clearCampaments()
      clearModules()
    }
  }, [])

  const onSubmit = async (dataForm: any) => {
    const dto: CensusHistoryModel = {
      CampamentoId: dataForm.CampamentoId,
      ModuloId: dataForm.ModuloId,
      FechCenso: dataForm.FechCenso,
      pageNumber: 1,
      size: 100,
    }
    setSearching(true)
    setSelectedCensus(null)
    setDataCensus([])
    try {
      const {data: response} = await CENSUS_SERVICES.reportCensus(dto)
      if (!response.error) {
        const mappedData = response.data.map((item: CensusHistoryResponse) => {
          const pendant =
            item.CamasPendientes === item.CamasTotales &&
            item.CamasTendidas === 0 &&
            item.CamasDestendidas === 0
          const complete =
            item.CamasDestendidas + item.CamasTendidas === item.CamasTotales &&
            item.CamasPendientes === 0

          return {
            ...item,
            Estado: pendant ? 'PENDIENTE' : complete ? 'COMPLETADO' : 'INCOMPLETO',
          }
        })
        setDataCensus(mappedData)
      } else {
        setDataCensus([])
      }
    } catch (error) {
      console.log({error})
    } finally {
      setSearching(false)
    }
  }

  const handleClear = () => {
    setDataCensus([])
    setSelectedCensus(null)
    setValue('CompaniaId', '')
    setValue('ClienteId', '')
    setValue('SiteId', '')
    setValue('CampamentoId', '')
    setValue('ModuloId', CENSUS_MODULO_TODOS_VALUE)
    setValue('FechCenso', getTodayDateInputValue())
    clearClients()
    clearSites()
    clearCampaments()
    clearModules()
  }

  const handleCompanyChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('CompaniaId', value)
    setValue('ClienteId', '')
    setValue('SiteId', '')
    clearSites()
    setValue('CampamentoId', '')
    clearCampaments()
    setError('CompaniaId', {})
    clearClients()
    clearModules()
    getClients({isCombo: true, companiaId: value}, 1)
  }

  const handleClientChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('ClienteId', value)
    setValue('SiteId', '')
    setValue('CampamentoId', '')
    clearSites()
    clearCampaments()
    clearModules()
    setError('ClienteId', {})
    getSites({isCombo: true, clienteId: value}, 1)
  }

  const handleSiteChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('SiteId', value)
    setValue('CampamentoId', '')
    setError('SiteId', {})
    clearCampaments()
    clearModules()
    getCampaments({isCombo: true, siteId: value}, 1)
  }

  const handleCampamentChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('CampamentoId', value)
    setError('CampamentoId', {})
    clearModules()
    getModules(
      {CampamentoId: value, pageNumber: 1, size: 100},
      {all: true, value: '4EsB2NAAsDA=', label: 'TODOS'},
    )
  }

  const handleDescargarExcel = async (dataForm: any) => {
    try {
      setIsDownloadXLS(true)
      const dto: CensusHistoryModel = {
        CampamentoId: dataForm.CampamentoId,
        ModuloId: dataForm.ModuloId,
        FechCenso: dataForm.FechCenso,
        pageNumber: 1,
        size: 100,
      }
      const response = await CENSUS_SERVICES.reportExcelCensus(dto)
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      moment().format('DD/MM/YY HH:mm').toString()
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `Histórico de Censos-${moment().format('DD/MM/YY HH:mm').toString()}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setIsDownloadXLS(false)
      getSnackBarToast('Se descargó el documento con éxito', 'success')
    } catch (error) {
      setIsDownloadXLS(false)
      getSnackBarToast('Hubo un error al descargar', 'error')
    }
  }

  return (
    <>
      <KTCard shadow className='m-5'>
        <div className='p-6'>
          <FormProvider {...methods}>
            <form className='m-2'>
              <div className='d-flex row'>
                <div className='mb-2 col-sm-12 col-md-4 col-xl-3'>
                  <SelectWrapper
                    labelText='Compañia'
                    name='CompaniaId'
                    data={companies}
                    onChangeValue={handleCompanyChange}
                    required
                  />
                </div>
                <div className='mb-2 col-sm-12 col-md-4 col-xl-3'>
                  <SelectWrapper
                    labelText='Cliente'
                    name='ClienteId'
                    data={clients}
                    onChangeValue={handleClientChange}
                    required
                  />
                </div>
                <div className='mb-2 col-sm-12 col-md-4 col-xl-3'>
                  <SelectWrapper
                    labelText='Site'
                    name='SiteId'
                    data={sites}
                    onChangeValue={handleSiteChange}
                    required
                  />
                </div>
                <div className='mb-2 col-sm-12 col-md-4 col-xl-3'>
                  <SelectWrapper
                    labelText='Campamento'
                    name='CampamentoId'
                    data={campaments}
                    onChangeValue={handleCampamentChange}
                    required
                  />
                </div>
                <div className='mb-2 col-sm-12 col-md-4 col-xl-3'>
                  <SelectWrapper labelText='Módulo' name='ModuloId' data={modules} required />
                </div>
                <div className='mb-2 col-sm-12 col-md-4 col-xl-3'>
                  <InputDateWrapper
                    labelText='Fecha de Censo'
                    name='FechCenso'
                    minValue='1900-01-01'
                    required
                  />
                </div>
              </div>
              <ButtonWrapper className={'sdx-white-bg'}>
                <Button
                  label='Buscar'
                  onClick={handleSubmit(onSubmit)}
                  loading={searching || isDownloadXLS}
                  disabled={searching || isDownloadXLS}
                />
                <Button label='Limpiar' onClick={handleClear} disabled={searching || isDownloadXLS}/>
                <button
                  type='button'
                  className='sdx_btn'
                  disabled={searching || isDownloadXLS}
                  onClick={handleSubmit(handleDescargarExcel)}
                >
                  {isDownloadXLS && (
                    <div className='icon-container' style={{margin: '0px 10px 0px 0px'}}>
                      <i className='loader'></i>
                    </div>
                  )}
                  Descargar
                </button>
              </ButtonWrapper>
            </form>
          </FormProvider>
        </div>
        <CensusHistoryTable
          data={dataCensus}
          loading={searching}
          setLoading={setSearching}
          setSelectedCensus={setSelectedCensus}
          selectedCensus={selectedCensus}
        />
      </KTCard>
    </>
  )
}

export {CensusHistoryForm}
